'use client';

import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { memo, useCallback, useState } from 'react';
import { LuGitFork, LuPlus, LuSettings } from 'react-icons/lu';

import { NODE_WIDTH_PX, ROUTING_PATH_HEIGHT_PX } from '@features/workflows/constants';
import { PaymentRoutingPolicy, PickerMode, type RoutingNodeData } from '@features/workflows/types';
import { useWorkflowCallbacks } from '@features/workflows/WorkflowContext';

import NodeHoverActions from './NodeHoverActions';

interface RoutingNodeComponentProps extends NodeProps {
  data: RoutingNodeData;
}

const POLICY_LABEL: Record<PaymentRoutingPolicy, string> = {
  [PaymentRoutingPolicy.ReleaseOnArrival]: 'Releases as funds arrive',
  [PaymentRoutingPolicy.WaitForAll]: 'Waits for all collections',
};

const RoutingNode = ({ id, data, dragging }: RoutingNodeComponentProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onOpenPicker, onOpenConfig, onDeleteNode, onAddSupplierBranch } = useWorkflowCallbacks();
  const { label, paths, policy, stagedUsdc } = data;

  const handleAdd = useCallback((nodeId: string) => onOpenPicker(nodeId, PickerMode.Add), [onOpenPicker]);
  const handleReplace = useCallback((nodeId: string) => onOpenPicker(nodeId, PickerMode.Replace), [onOpenPicker]);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleConfig = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenConfig(id);
    },
    [onOpenConfig, id],
  );
  const handleAddBranch = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddSupplierBranch(id);
    },
    [onAddSupplierBranch, id],
  );

  const policyLabel = POLICY_LABEL[policy ?? PaymentRoutingPolicy.ReleaseOnArrival];

  return (
    <Box position="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <NodeHoverActions
        nodeId={id}
        isHovered={isHovered && dragging !== true}
        onAdd={handleAdd}
        onReplace={handleReplace}
        onDelete={onDeleteNode}
      />
      <Flex
        direction="column"
        bg="surface.background"
        borderWidth="1px"
        borderColor="border.light"
        borderRadius="xl"
        w={`${NODE_WIDTH_PX}px`}
        overflow="hidden"
        boxShadow="xs"
        _hover={{ boxShadow: 'sm' }}
        cursor="pointer"
        onClick={handleConfig}
      >
        {/* Header */}
        <Flex px={4} pt={3} pb={3} gap={3} alignItems="flex-start">
          <Flex
            boxSize="28px"
            borderRadius="md"
            bg="surface.muted"
            alignItems="center"
            justifyContent="center"
            color="text.secondary"
            flexShrink={0}
          >
            <LuGitFork size={15} />
          </Flex>
          <Flex direction="column" gap={0.5} minW={0} flex={1}>
            <Text fontSize="sm" fontWeight="semibold" truncate>
              {label}
            </Text>
            <Text fontSize="xs" color="text.secondary" truncate>
              {policyLabel}
            </Text>
          </Flex>
          <IconButton
            size="2xs"
            variant="ghost"
            color="text.secondary"
            _hover={{ color: 'text.primary' }}
            aria-label="Configure routing"
            onClick={handleConfig}
            flexShrink={0}
          >
            <LuSettings size={14} />
          </IconButton>
        </Flex>
        {/* Staging pool */}
        <Flex
          mx={4}
          mb={3}
          px={3}
          py={2}
          borderRadius="lg"
          bg="surface.muted"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text fontSize="xs" color="text.secondary">
            Staging pool
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            {(stagedUsdc ?? 0).toLocaleString('en-US')} USDC
          </Text>
        </Flex>
        {/* Ranked suppliers */}
        <Flex px={4} pb={1} alignItems="center" justifyContent="space-between">
          <Text fontSize="2xs" letterSpacing="wide" textTransform="uppercase" color="text.secondary">
            Suppliers
          </Text>
          <Button
            size="2xs"
            variant="ghost"
            color="text.secondary"
            _hover={{ color: 'text.primary' }}
            onClick={handleAddBranch}
          >
            <LuPlus size={12} />
            Add
          </Button>
        </Flex>
        <Flex direction="column" px={3} pb={3} gap={1.5}>
          {paths.map((path, index) => (
            <Flex
              key={path.id}
              bg="surface.muted"
              borderRadius="lg"
              pl={2}
              pr={3}
              h={`${ROUTING_PATH_HEIGHT_PX}px`}
              alignItems="center"
              gap={2.5}
              position="relative"
            >
              <Flex
                boxSize="20px"
                borderRadius="full"
                bg="surface.background"
                alignItems="center"
                justifyContent="center"
                fontSize="2xs"
                fontWeight="semibold"
                color="text.secondary"
                flexShrink={0}
              >
                {index + 1}
              </Flex>
              <Text fontSize="xs" fontWeight="medium" truncate flex={1} minW={0}>
                {path.label}
              </Text>
              {path.amountUsdc != null && (
                <Text fontSize="xs" color="text.secondary" flexShrink={0}>
                  {path.amountUsdc.toLocaleString('en-US')} USDC
                </Text>
              )}
              <Handle
                type="source"
                position={Position.Right}
                id={`path-${path.id}`}
                style={{
                  top: 'auto',
                  width: 8,
                  height: 8,
                  background: 'var(--chakra-colors-gray-400)',
                  border: '2px solid var(--chakra-colors-gray-200)',
                  right: -4,
                }}
              />
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 8,
          height: 8,
          background: 'var(--chakra-colors-gray-400)',
          border: '2px solid var(--chakra-colors-gray-200)',
        }}
      />
    </Box>
  );
};

export default memo(RoutingNode);
